<script setup lang="ts">
  import { Floating, Icon, Typo, TypoVariant, Button, ButtonSize } from '@travelata-libs/ui-kit';
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import AccountHint from './AccountHint.vue';
  import AccountMenu from './AccountMenu.vue';
  import { ACCOUNT_TEXTS } from '../config/texts';
  import type { AccountMenuKey, AccountUser } from '../model/types';
  import { useHeaderAccount } from '../model/useHeaderAccount';

  interface HeaderAccountProps {
    user?: AccountUser | null;
    accountUrl?: string;
    bordered?: boolean;
    buttonSize?: `${ButtonSize}`;
    // Хост оставляет только те пункты меню, которые у него есть: на витринах нет Турхантера.
    menuKeys?: AccountMenuKey[];
  }

  const props = withDefaults(defineProps<HeaderAccountProps>(), {
    user: null,
    accountUrl: '',
    bordered: true,
    buttonSize: ButtonSize.S,
    menuKeys: undefined,
  });

  interface HeaderAccountEmits {
    (e: 'login'): void;
    (e: 'logout'): void;
  }

  const emit = defineEmits<HeaderAccountEmits>();

  const core = useHeaderAccount({
    initialUser: props.user ?? null,
    initialAccountUrl: props.accountUrl ?? '',
  });

  const visibleLinks = computed(() => {
    const keys = props.menuKeys;

    return keys ? core.resolvedLinks.value.filter((link) => keys.includes(link.key)) : core.resolvedLinks.value;
  });

  const floatingRef = ref<{ hide: () => void } | null>(null);
  const overlayOpen = ref(false);

  const onChangeVisible = (visible: boolean): void => {
    overlayOpen.value = visible;
  };

  const onLogin = (): void => {
    if (!core.isAuthorized.value) {
      emit('login');
    }
  };

  const onLogout = (): void => {
    emit('logout');
    floatingRef.value?.hide();
  };

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      floatingRef.value?.hide();
    }
  };

  onMounted(() => {
    watch(
      overlayOpen,
      (open) => {
        if (open) {
          document.addEventListener('keydown', onKeydown);
        } else {
          document.removeEventListener('keydown', onKeydown);
        }
      },
      { immediate: true },
    );
  });

  watch(
    () => props.user,
    (u) => core.setUser(u ?? null),
    { immediate: true },
  );

  watch(
    () => props.accountUrl,
    (url) => core.setAccountUrl(url ?? ''),
    { immediate: true },
  );

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown);
  });
</script>

<template>
  <div
    :class="['header-account', `header-account_size-${buttonSize}`, { 'header-account_no-bordered': !bordered }]"
    data-testid="header-account"
  >
    <Floating
      :key="core.isAuthorized.value ? 'auth' : 'guest'"
      ref="floatingRef"
      trigger="hover"
      placement="bottom-end"
      :teleported="false"
      @change-visible="onChangeVisible"
    >
      <template #reference>
        <Button
          v-if="!core.isAuthorized.value"
          type="button"
          class="header-account__button"
          appearance="sub-primary"
          data-testid="header-account-login"
          icon-left="user"
          :size="buttonSize"
          @click="onLogin"
        >
          {{ ACCOUNT_TEXTS.loginLabel }}
        </Button>
        <div
          v-else
          class="header-account__control-container"
        >
          <div
            class="header-account__control"
            data-testid="header-account-user"
          >
            <Typo :variant="TypoVariant.BodyMRegular">{{ core.displayName.value }}</Typo>
          </div>

          <Icon
            :class="[
              'header-account__icon',
              {
                'header-account__icon_open': overlayOpen,
              },
            ]"
            name="chevron-down"
          ></Icon>
        </div>
      </template>

      <template #floating>
        <div>
          <AccountHint v-if="!core.isAuthorized.value" />
          <AccountMenu
            v-else
            :links="visibleLinks"
            @logout="onLogout"
          />
        </div>
      </template>
    </Floating>
  </div>
</template>

<style scoped lang="scss">
  .header-account {
    $root: &;

    &_no-bordered {
      #{$root}__button {
        border: none;
        padding: 0;
      }
    }

    &__button {
      background-color: transparent;
      border: 1px solid var(--color-text-on-brand);
      color: var(--color-text-on-brand);
    }

    &__icon {
      fill: var(--color-text-on-brand);
      transition: var(--transition-default);
    }

    &__control-container {
      align-items: center;
      cursor: pointer;
      display: flex;
      gap: var(--spacing-xxs);
    }

    // Аватар ниже кнопки входа: при крупной кнопке строка шапки держит её высоту,
    // чтобы вход и выход не сдвигали всё, что ниже шапки.
    &_size-l #{$root}__control-container {
      min-height: 48px;
    }

    &__icon_open {
      transform: rotate(180deg);
    }

    &__control {
      align-items: center;
      background-color: var(--color-bg-secondary);
      border-radius: 100%;
      color: var(--color-text-secondary);
      display: flex;
      height: 32px;
      justify-content: center;
      text-transform: uppercase;
      width: 32px;
    }
  }
</style>
